/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { booleanAttribute, Component, Input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { uniqueId } from 'lodash-es';
import { BadgeComponent } from '../badge/badge.component';
import { BescheidStatusTextComponent } from '../bescheid-status-text/bescheid-status-text.component';
import { DateComponent } from '../date/date.component';
import { IconComponent } from '../icon/icon.component';
import { MailIconComponent } from '../icons/mail-icon/mail-icon.component';
import { UserIconComponent } from '../user-icon/user-icon.component';
import { VorgangNumberComponent } from '../vorgang-number/vorgang-number.component';
import { VorgangStatusComponent, VorgangStatusLabel } from '../vorgang-status/vorgang-status.component';

@Component({
  selector: 'ods-vorgang-card',
  imports: [
    DateComponent,
    RouterLink,
    VorgangStatusComponent,
    VorgangNumberComponent,
    BadgeComponent,
    MailIconComponent,
    IconComponent,
    BescheidStatusTextComponent,
    UserIconComponent,
  ],
  template: `<div
      class="z-10 flex flex-col justify-between gap-4 @xl:flex-row"
      [id]="upperRowId"
      data-test-id="vorgang-card-upper-row"
    >
      <div class="flex flex-wrap gap-x-6 gap-y-4 pl-1">
        <ods-vorgang-status [status]="status" data-test-id="vorgang-card-status" />
        @if (shouldShowApprovalStatus) {
          <ods-bescheid-status-text
            [dateText]="approvalDate"
            [bewilligt]="approved"
            [withLabel]="false"
            size="medium-small"
            data-test-id="vorgang-card-bescheid-status"
          />
        }
        @if (archived) {
          <p class="flex items-center gap-1" data-test-id="vorgang-card-archived">
            <ods-icon name="verakten" [config]="{ fill: 'text' }" />in Veraktung
          </p>
        }
      </div>
      <div class="relative flex flex-row flex-wrap items-center gap-6 self-end @md:flex-nowrap">
        <div class="flex flex-row">
          @if (wiedervorlage) {
            <p class="text-error flex gap-2" data-test-id="vorgang-card-deadline" title="Wiedervorlage">
              <ods-icon name="update" [config]="{ fill: 'error' }" />
              <span><span class="sr-only">Wiedervorlage: </span><ods-date [date]="wiedervorlage" /></span>
            </p>
          }
        </div>
        <ods-badge
          color="success"
          icon="flag"
          [class.invisible]="!hasPriority"
          aria-label="Vorgang markiert"
          role="img"
          data-test-id="vorgang-card-priority-badge"
        />
        <ods-mail-icon
          [withBadge]="hasNewMessage"
          [class.invisible]="!hasMessage"
          [attr.aria-label]="hasNewMessage ? 'Neue Nachrichten vorhanden' : 'Nachrichten vorhanden'"
          [title]="hasNewMessage ? 'Neue Nachrichten' : 'Nachrichten'"
          data-test-id="vorgang-card-new-messages-icon"
        />
        <ods-user-icon
          [initials]="userInitials"
          [isNotFound]="isUserNotFound"
          [title]="userTitle"
          [ariaLabel]="'Aktuell zugewiesener Nutzer: ' + userTitle"
          data-test-id="vorgang-card-user-icon"
        />
        <ng-content select="[action]" />
      </div>
    </div>
    <div class="flex flex-col gap-4 @4xl:flex-row">
      <div class="flex flex-2 flex-col gap-1">
        @if (date) {
          <ods-date [date]="date" withTime="true" data-test-id="vorgang-card-date" />
        }
        <div class="flex flex-col gap-2">
          <a
            class="group/link outline-hidden after:absolute after:inset-0 after:block after:content-['']"
            [routerLink]="linkTo"
            [attr.aria-describedby]="upperRowId"
          >
            <p class="group-hover/link:underline group-focus-visible/link:underline">
              {{ antragsteller }}
              @if (birthDate) {
                <span data-test-id="vorgang-card-birth-date"> * <ods-date [date]="birthDate" /></span>
              }
            </p>
            <strong class="text-lg font-medium group-hover/link:underline group-focus-visible/link:underline">{{ name }}</strong>
          </a>
          <div class="flex flex-col gap-1 empty:hidden">
            @if (vorgangNumber) {
              <ods-vorgang-number [text]="vorgangNumber" type="nummer" />
            }
            @if (aktenzeichen) {
              <ods-vorgang-number [text]="aktenzeichen" type="aktenzeichen" />
            }
          </div>
        </div>
      </div>
      <div class="flex-1"><ng-content /></div>
    </div>`,
  host: {
    class:
      'text-text hover:bg-background-100 focus-within:outline-focus @container relative flex flex-col gap-2 p-4 focus-within:z-10 focus-within:outline-2 focus-within:outline-offset-2 focus-within:has-[button:focus]:outline-hidden has-[button:hover]:bg-inherit',
    role: 'listitem',
  },
})
export class VorgangCardComponent {
  @Input({ required: true }) name!: string;
  @Input({ required: true }) linkTo!: string;
  @Input() date: Date;
  @Input() antragsteller: string;
  @Input() birthDate: Date;
  @Input() userInitials: string;
  @Input({ required: true }) userTitle!: string;
  @Input({ transform: booleanAttribute }) isUserNotFound: boolean = false;
  @Input({ required: true }) status!: VorgangStatusLabel;
  @Input() vorgangNumber: string;
  @Input() aktenzeichen: string;
  @Input() approvalDate: string;
  @Input() wiedervorlage: Date;
  @Input({ transform: booleanAttribute }) hasPriority: boolean = false;
  @Input({ transform: booleanAttribute }) hasMessage: boolean = false;
  @Input({ transform: booleanAttribute }) hasNewMessage: boolean = false;
  @Input({ transform: booleanAttribute }) archived: boolean = false;
  @Input({ transform: booleanAttribute }) approved: boolean;

  public readonly upperRowId = uniqueId('vorgang-card-upper-row-');

  get shouldShowApprovalStatus(): boolean {
    return this.approvalDate && this.approved !== undefined;
  }
}
